import { Box } from "@mantine/core";
import { useAtom } from "jotai";
import { TFunction } from "react-i18next";
import { colors } from "theme";

import Button from "components/Button";
import Flex from "components/Flex";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { windowsPOIAtom } from "atoms/windowsPOI";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { PointOfImpactGlass } from "types/DigitalJourneySession";

export const getWindowsPointOfImpact = (t: TFunction) => [
  {
    label: "Parabrezza anteriore",
    value: PointOfImpactGlass.FRONT_WINDOW,
  },
  {
    label: "Lunotto",
    value: PointOfImpactGlass.REAR_WINDOW,
  },
  {
    label: "Finestrino anteriore sinistro",
    value: PointOfImpactGlass.LEFT_FRONT_WINDOW,
  },
  {
    label: "Finestrino anteriore destro",
    value: PointOfImpactGlass.RIGHT_FRONT_WINDOW,
  },
  {
    label: "Finestrino posteriore sinistro",
    value: PointOfImpactGlass.RIGHT_REAR_WINDOW,
  },
  {
    label: "Finestrino posteriore destro",
    value: PointOfImpactGlass.LEFT_REAR_WINDOW,
  },
  {
    label: "Deflettore anteriore sinistro",
    value: PointOfImpactGlass.LEFT_FRONT_DEFLECTOR,
  },
  {
    label: "Deflettore anteriore destro",
    value: PointOfImpactGlass.RIGHT_FRONT_DEFLECTOR,
  },
  {
    label: "Deflettore posteriore sinistro",
    value: PointOfImpactGlass.LEFT_REAR_DEFLECTOR,
  },
  {
    label: "Deflettore posteriore destro",
    value: PointOfImpactGlass.RIGHT_REAR_DEFLECTOR,
  },
];

export const DJSelectWindowsDamaged = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext, continueCTA } = useDigitalJourneyPageRouting();
  const [windowsPOI, setWindowsPOI] = useAtom(windowsPOIAtom);

  const isDisabled = windowsPOI.length === 0;

  const onContinue = () => {
    trackEvent({ name: "Digital Journey Select Windows Damaged - continue" });
    onNext();
  };

  return (
    <PageLayout contentSpacing="space-between">
      <Flex px={24} py={24} gap={16} flex={1} mb={16}>
        <Text as="h1" sizeVariant="xl" weight={600}>
          Seleziona i cristalli danneggiati dal sinistro
        </Text>
        <Box>
          <Flex gap={16} role="radiogroup" aria-labelledby="typeOfClaim">
            {getWindowsPointOfImpact(t).map((option) => (
              <Button
                aria-labelledby={`typeOfClaim-${option.label}-label`}
                aria-checked={windowsPOI.includes(option.value)}
                role="radio"
                key={option.label}
                onClick={() => {
                  if (windowsPOI.includes(option.value)) {
                    setWindowsPOI((prevState) => prevState.filter((poi) => poi !== option.value));
                  } else {
                    setWindowsPOI((prevState) => [option.value, ...prevState]);
                  }
                }}
                typeVariant={windowsPOI.includes(option.value) ? "primary" : "secondary"}
                sizeVariant="small"
                sx={{
                  "& div": {
                    justifyContent: "flex-start",
                    fontWeight: 400,
                    color: windowsPOI.includes(option.value) ? colors.white : colors.grey[90],
                  },
                }}
              >
                <label id={`typeOfClaim-${option.label}-label`}>{option.label}</label>
              </Button>
            ))}
          </Flex>
        </Box>
      </Flex>
      <Flex px={24} py={24} gap={16}>
        <Button disabled={isDisabled} onClick={onContinue}>
          {continueCTA}
        </Button>
      </Flex>
    </PageLayout>
  );
};
